import { createApp } from 'vue'
import { createPinia } from 'pinia'
import './style.css'
import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/authStore'
import { useAppStore } from './stores/appStore'
import { setupResilienceListeners, isChunkLoadError, handleChunkLoadFailure } from './utils/resilience'
import { primeAppHistory, initHistoryGuard } from './composables/useAppNavigation'

setupResilienceListeners()

const app = createApp(App)
const pinia = createPinia()

app.config.errorHandler = (err, _instance, info) => {
  console.error('[Vue Global Error Handler]', err, info)
  if (isChunkLoadError(err)) {
    handleChunkLoadFailure()
  }
}

app.use(pinia)

const isAutomatedOrBot = (): boolean => {
  if (typeof navigator === 'undefined') return false
  if ((navigator as any).webdriver) return true
  const ua = navigator.userAgent || ''
  return /bot|googlebot|crawler|spider|robot|crawling|lighthouse|headless|pagespeed|ptst/i.test(ua)
}



const initApp = async () => {
  const authStore = useAuthStore()
  const appStore = useAppStore()

  if (isAutomatedOrBot()) {
    // Automated crawlers & Lighthouse have no session; mount immediately with demo mode
    appStore.loadDemoData()
    app.use(router)
    await router.isReady()
    app.mount('#app')
    return
  }

  const hasStoredSession = authStore.hasStoredAuthSession()

  if (hasStoredSession) {
    // Returning user: Wait for auth and initial data sync while keeping splash screen visible
    try {
      await authStore.initializeAuth()
      if (authStore.user) {
        await appStore.waitForInitialSync(2500)
      } else {
        // Stored session could not be restored (expired/revoked) -> fall back to demo mode
        appStore.loadDemoData()
      }
    } catch (err) {
      console.warn('Auth initialization warning, continuing app mount:', err)
      if (!authStore.user) {
        appStore.loadDemoData()
      }
    }
  } else {
    // First time visitor / guest: Load demo mode immediately for fast startup
    appStore.loadDemoData()
    // Initialize auth in background
    authStore.initializeAuth().catch((err) => {
      console.warn('Background auth warning:', err)
    })
  }

  app.use(router)
  await router.isReady()
  primeAppHistory(router)
  initHistoryGuard(router)
  app.mount('#app')

  // Handle incoming PWA deep links (e.g. from Galaxy Watch "Auf Handy öffnen")
  if ('launchQueue' in window && typeof (window as any).launchQueue?.setConsumer === 'function') {
    ;(window as any).launchQueue.setConsumer((launchParams: any) => {
      if (launchParams?.targetURL) {
        try {
          const url = new URL(launchParams.targetURL)
          const target = url.pathname + url.search + url.hash
          if (target && target !== '/' && target !== router.currentRoute.value.fullPath) {
            router.push(target)
          }
        } catch (err) {
          console.warn('[PWA LaunchQueue] Navigation error:', err)
        }
      }
    })
  }
}

initApp()
